import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUnreadMessageCount } from '../../services/messages';
import { getSupportTicketUnreadCount } from '../../services/supportTickets';
import { getReviewQueueCount, getApprovalQueueCount, getAwaitingReviewCount } from '../../services/licences';
import { getQuotationStats } from '../../services/quotations';

const navigation = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    requiredPermission: 'dashboard:read',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M7.5 9.16667V15.8333" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12.5 4.16667V15.8333" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2.5 10.8333L7.5 9.16667L12.5 12.5L17.5 10.8333" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'COA Library',
    path: '/coa-library',
    requiredPermission: 'coa:read', // Match the API permission requirement
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M16.6667 5.83333H3.33333C2.8731 5.83333 2.5 6.20643 2.5 6.66667V15C2.5 15.4602 2.8731 15.8333 3.33333 15.8333H16.6667C17.1269 15.8333 17.5 15.4602 17.5 15V6.66667C17.5 6.20643 17.1269 5.83333 16.6667 5.83333Z" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M13.3333 5.83333V4.16667C13.3333 3.72464 13.1577 3.30072 12.8452 2.98816C12.5326 2.67559 12.1087 2.5 11.6667 2.5H8.33333C7.89131 2.5 7.46738 2.67559 7.15482 2.98816C6.84226 3.30072 6.66667 3.72464 6.66667 4.16667V5.83333" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: '3rd Party COAs',
    path: '/third-party-coas',
    requiredPermission: 'thirdpartycoa:read',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M4.16667 3.33333H15.8333C16.7538 3.33333 17.5 4.07952 17.5 5V15C17.5 15.9205 16.7538 16.6667 15.8333 16.6667H4.16667C3.24619 16.6667 2.5 15.9205 2.5 15V5C2.5 4.07952 3.24619 3.33333 4.16667 3.33333Z" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6.66667 1.66667V4.16667" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M13.3333 1.66667V4.16667" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2.5 6.66667H17.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'Requested Licences',
    path: '/requested-licences',
    requiredPermission: 'licence:read',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M10.8333 1.66667L2.5 11.6667H10L9.16667 18.3333L17.5 8.33333H10L10.8333 1.66667Z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'My Review Queue',
    path: '/my-review-queue',
    requiredPermission: 'licence:review',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M10 1.66667L12.5 6.66667L18.3333 7.5L14.1667 11.6667L15 17.5L10 14.1667L5 17.5L5.83333 11.6667L1.66667 7.5L7.5 6.66667L10 1.66667Z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'My Approval Queue',
    path: '/my-approval-queue',
    requiredPermission: 'licence:approve',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M16.6667 2.5L7.5 11.6667L3.33333 7.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16.6667 2.5H10.8333V8.33333H16.6667V2.5Z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'Invoices',
    path: '/invoices',
    requiredPermission: 'transaction:read',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M15.8333 1.66667H4.16667C3.24619 1.66667 2.5 2.41286 2.5 3.33333V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V3.33333C17.5 2.41286 16.7538 1.66667 15.8333 1.66667Z" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5.83333 6.66667H14.1667" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5.83333 10H14.1667" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5.83333 13.3333H10" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'Quotations',
    path: '/quotations',
    requiredPermission: 'quotation:read',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M17.5 5H2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M15.8333 1.66667H4.16667C3.24619 1.66667 2.5 2.41286 2.5 3.33333V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V3.33333C17.5 2.41286 16.7538 1.66667 15.8333 1.66667Z" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5.83333 8.33333H14.1667" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5.83333 11.6667H14.1667" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5.83333 15H14.1667" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'Customers',
    path: '/customers',
    requiredPermission: 'customer:read',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M16.6667 17.5V15.8333C16.6667 14.9493 16.3155 14.1014 15.6904 13.4763C15.0653 12.8512 14.2174 12.5 13.3333 12.5H6.66667C5.78261 12.5 4.93476 12.8512 4.30964 13.4763C3.68452 14.1014 3.33333 14.9493 3.33333 15.8333V17.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10 9.16667C11.841 9.16667 13.3333 7.67428 13.3333 5.83333C13.3333 3.99238 11.841 2.5 10 2.5C8.15905 2.5 6.66667 3.99238 6.66667 5.83333C6.66667 7.67428 8.15905 9.16667 10 9.16667Z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'CMS',
    path: '/cms',
    requiredPermission: 'cms:read',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M16.6667 2.5H3.33333C2.8731 2.5 2.5 2.8731 2.5 3.33333V16.6667C2.5 17.1269 2.8731 17.5 3.33333 17.5H16.6667C17.1269 17.5 17.5 17.1269 17.5 16.6667V3.33333C17.5 2.8731 17.1269 2.5 16.6667 2.5Z" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7.5 6.66667H12.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7.5 10H12.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7.5 13.3333H10" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'Messages',
    path: '/messages',
    requiredPermission: 'message:read',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M17.5 9.58333C17.5029 10.6832 17.2459 11.7682 16.75 12.75C16.162 13.9265 15.2581 14.916 14.1395 15.6077C13.021 16.2995 11.7319 16.6662 10.4167 16.6667C9.31678 16.6695 8.23176 16.4126 7.25 15.9167L2.5 17.5L4.08333 12.75C3.58744 11.7682 3.33047 10.6832 3.33333 9.58333C3.33384 8.26813 3.70051 6.97904 4.39227 5.86045C5.08402 4.74187 6.07355 3.83797 7.25 3.25C8.23176 2.75411 9.31678 2.49713 10.4167 2.5H10.8333C12.5703 2.59583 14.2109 3.32899 15.4409 4.55907C16.671 5.78915 17.4042 7.42973 17.5 9.16667V9.58333Z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },

];

const settingsNavigation = [
  {
    name: 'Personal Details',
    path: '/settings/personal-details',
    requiredPermission: 'profile:read',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M16.6667 17.5V15.8333C16.6667 14.9493 16.3155 14.1014 15.6904 13.4763C15.0653 12.8512 14.2174 12.5 13.3333 12.5H6.66667C5.78261 12.5 4.93476 12.8512 4.30964 13.4763C3.68452 14.1014 3.33333 14.9493 3.33333 15.8333V17.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10 9.16667C11.841 9.16667 13.3333 7.67428 13.3333 5.83333C13.3333 3.99238 11.841 2.5 10 2.5C8.15905 2.5 6.66667 3.99238 6.66667 5.83333C6.66667 7.67428 8.15905 9.16667 10 9.16667Z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'Notifications',
    path: '/settings/notifications',
    requiredPermission: 'profile:read',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M15 6.66667C15 5.34058 14.4732 4.06881 13.5355 3.13113C12.5979 2.19347 11.3261 1.66667 10 1.66667C8.67392 1.66667 7.40215 2.19347 6.46447 3.13113C5.52678 4.06881 5 5.34058 5 6.66667V9.16667L3.33333 10.8333V12.5H16.6667V10.8333L15 9.16667V6.66667Z" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7.5 12.5V13.3333C7.5 14.2174 7.85119 15.0653 8.47631 15.6904C9.10143 16.3155 9.94928 16.6667 10.8333 16.6667C11.7174 16.6667 12.5652 16.3155 13.1904 15.6904C13.8155 15.0653 14.1667 14.2174 14.1667 13.3333V12.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'Admin Team',
    path: '/settings/admin-team',
    requiredPermission: 'user:read',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M7.5 18.3333H12.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10 15V18.3333" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M15.8333 7.5H4.16667C3.24619 7.5 2.5 8.24619 2.5 9.16667V15C2.5 15.9205 3.24619 16.6667 4.16667 16.6667H15.8333C16.7538 16.6667 17.5 15.9205 17.5 15V9.16667C17.5 8.24619 16.7538 7.5 15.8333 7.5Z" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5.83333 7.5V4.16667C5.83333 3.50363 6.09672 2.86774 6.56557 2.3989C7.03441 1.93006 7.6703 1.66667 8.33333 1.66667H11.6667C12.3297 1.66667 12.9656 1.93006 13.4344 2.3989C13.9033 2.86774 14.1667 3.50363 14.1667 4.16667V7.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'Roles & Permissions',
    path: '/settings/roles-permissions',
    requiredPermission: 'role:read',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M10 1.66667L12.5 6.66667L18.3333 7.5L14.1667 11.6667L15 17.5L10 14.1667L5 17.5L5.83333 11.6667L1.66667 7.5L7.5 6.66667L10 1.66667Z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    name: 'Xero Integration',
    path: '/settings/xero',
    requiredPermission: '*',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M10 2.5L2.5 7.5L10 12.5L17.5 7.5L10 2.5Z" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2.5 12.5L10 17.5L17.5 12.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2.5 7.5L10 12.5L17.5 7.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  }
];

const Sidebar = () => {
  const { user } = useAuth();
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [supportTicketUnread, setSupportTicketUnread] = useState(0);
  const [reviewQueueCount, setReviewQueueCount] = useState(0);
  const [approvalQueueCount, setApprovalQueueCount] = useState(0);
  const [awaitingReviewCount, setAwaitingReviewCount] = useState(0);
  const [quotationAttentionCount, setQuotationAttentionCount] = useState(0);

  // Fetch unread message count
  useEffect(() => {
    let interval = null;
    let timeout = null;
    
    const fetchUnreadCount = async () => {
      // Only fetch if page is visible
      if (document.hidden) {
        return;
      }
      
      try {
        const [response, supportRes] = await Promise.all([
          getUnreadMessageCount(),
          getSupportTicketUnreadCount().catch(() => null)
        ]);
        if (response && response.success !== false && response.data) {
          setUnreadMessageCount((response.data.unreadConversationsCount ?? response.data.unreadCount) || 0);
        }
        if (supportRes && supportRes.success !== false && supportRes.data) {
          setSupportTicketUnread(supportRes.data.unreadCount || 0);
        }
      } catch (error) {
        console.error('Error fetching unread message count:', error);
        // On error, increase interval to avoid rate limiting
        if (interval) {
          clearInterval(interval);
          // Retry after 2 minutes on error
          timeout = setTimeout(() => {
            interval = setInterval(fetchUnreadCount, 120000); // 2 minutes
          }, 120000);
        }
      }
    };

    // Initial fetch
    fetchUnreadCount();
    
    // Poll for new messages every 60 seconds (reduced frequency)
    interval = setInterval(fetchUnreadCount, 60000);
    
    // Stop polling when tab is hidden, resume when visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (interval) clearInterval(interval);
        if (timeout) clearTimeout(timeout);
      } else {
        // Resume polling when tab becomes visible
        fetchUnreadCount();
        if (!interval) {
          interval = setInterval(fetchUnreadCount, 60000);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Fetch review queue count
  useEffect(() => {
    let interval = null;
    let timeout = null;
    
    const fetchReviewQueueCount = async () => {
      if (document.hidden) {
        return;
      }
      
      try {
        const response = await getReviewQueueCount();
        if (response && response.success !== false && response.data) {
          setReviewQueueCount(response.data.count || 0);
        }
      } catch (error) {
        console.error('Error fetching review queue count:', error);
        if (interval) {
          clearInterval(interval);
          timeout = setTimeout(() => {
            interval = setInterval(fetchReviewQueueCount, 120000);
          }, 120000);
        }
      }
    };

    fetchReviewQueueCount();
    interval = setInterval(fetchReviewQueueCount, 60000);
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (interval) clearInterval(interval);
        if (timeout) clearTimeout(timeout);
      } else {
        fetchReviewQueueCount();
        if (!interval) {
          interval = setInterval(fetchReviewQueueCount, 60000);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Fetch quotation attention count (Pending + Under Review = new or awaiting review)
  useEffect(() => {
    let interval = null;
    let timeout = null;

    const fetchQuotationAttentionCount = async () => {
      if (document.hidden) return;
      try {
        const response = await getQuotationStats();
        if (response && response.success !== false && response.data) {
          const { pending = 0, underReview = 0 } = response.data;
          setQuotationAttentionCount((pending || 0) + (underReview || 0));
        }
      } catch (error) {
        console.error('Error fetching quotation stats:', error);
        if (interval) {
          clearInterval(interval);
          timeout = setTimeout(() => {
            interval = setInterval(fetchQuotationAttentionCount, 120000);
          }, 120000);
        }
      }
    };

    fetchQuotationAttentionCount();
    interval = setInterval(fetchQuotationAttentionCount, 60000);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (interval) clearInterval(interval);
        if (timeout) clearTimeout(timeout);
      } else {
        fetchQuotationAttentionCount();
        if (!interval) {
          interval = setInterval(fetchQuotationAttentionCount, 60000);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Fetch approval queue count
  useEffect(() => {
    let interval = null;
    let timeout = null;
    
    const fetchApprovalQueueCount = async () => {
      if (document.hidden) {
        return;
      }
      
      try {
        const response = await getApprovalQueueCount();
        if (response && response.success !== false && response.data) {
          setApprovalQueueCount(response.data.count || 0);
        }
      } catch (error) {
        console.error('Error fetching approval queue count:', error);
        if (interval) {
          clearInterval(interval);
          timeout = setTimeout(() => {
            interval = setInterval(fetchApprovalQueueCount, 120000);
          }, 120000);
        }
      }
    };

    fetchApprovalQueueCount();
    interval = setInterval(fetchApprovalQueueCount, 60000);
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (interval) clearInterval(interval);
        if (timeout) clearTimeout(timeout);
      } else {
        fetchApprovalQueueCount();
        if (!interval) {
          interval = setInterval(fetchApprovalQueueCount, 60000);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Fetch awaiting review count (Requested Licences - licences with status Awaiting Review)
  useEffect(() => {
    let interval = null;
    let timeout = null;

    const fetchAwaitingReviewCount = async () => {
      if (document.hidden) return;
      try {
        const response = await getAwaitingReviewCount();
        if (response && response.success !== false && response.data) {
          setAwaitingReviewCount(response.data.count || 0);
        }
      } catch (error) {
        console.error('Error fetching awaiting review count:', error);
        if (interval) {
          clearInterval(interval);
          timeout = setTimeout(() => {
            interval = setInterval(fetchAwaitingReviewCount, 120000);
          }, 120000);
        }
      }
    };

    fetchAwaitingReviewCount();
    interval = setInterval(fetchAwaitingReviewCount, 60000);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (interval) clearInterval(interval);
        if (timeout) clearTimeout(timeout);
      } else {
        fetchAwaitingReviewCount();
        if (!interval) {
          interval = setInterval(fetchAwaitingReviewCount, 60000);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const canAccess = (requiredPermission) => {
    if (!requiredPermission) return true;
    
    // If no user, deny access
    if (!user) return false;
    
    // Check if user has SuperAdmin role (grants all access)
    if (user.roles && Array.isArray(user.roles) && user.roles.includes('SuperAdmin')) {
      return true;
    }
    
    // If no permissions array, fallback to true to avoid locking out
    if (!user.permissions || user.permissions.length === 0) {
      // Only allow if user has roles (means permissions might not be loaded yet)
      return user.roles && user.roles.length > 0;
    }
    
    // SuperAdmin has '*' permission which grants access to everything
    if (user.permissions.includes('*')) {
      return true;
    }
    
    return user.permissions.includes(requiredPermission);
  };

  // Check if user is SuperAdmin
  const isSuperAdmin = user && user.roles && Array.isArray(user.roles) && user.roles.includes('SuperAdmin');

  const getLinkClass = (isActive) => `
    flex items-center gap-3 px-4 py-3 rounded-lg text-[14px] font-medium transition-all relative
    ${isActive 
      ? 'text-gray-900 font-semibold bg-white border-l-4 border-emerald-400' 
      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 border-l-4 border-transparent'
    }
  `;

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Navigation */}
      <div className="flex-1 px-3 pt-6 pb-6 overflow-y-auto custom-scrollbar">
        <nav className="space-y-1">
          {navigation.filter(item => canAccess(item.requiredPermission)).map((item) => (
            <NavLink
              key={item.name}
              to={item.path}
              className={({ isActive }) => getLinkClass(isActive)}
            >
              <span className="text-current">{item.icon}</span>
              <span className="flex-1">{item.name}</span>
              {item.name === 'Messages' && (unreadMessageCount + supportTicketUnread) > 0 && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[11px] font-semibold rounded-full animate-notification-glow">
                  {(unreadMessageCount + supportTicketUnread) > 99
                    ? '99+'
                    : unreadMessageCount + supportTicketUnread}
                </span>
              )}
              {item.name === 'Requested Licences' && awaitingReviewCount > 0 && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[11px] font-semibold rounded-full animate-notification-glow">
                  {awaitingReviewCount > 99 ? '99+' : awaitingReviewCount}
                </span>
              )}
              {item.name === 'My Review Queue' && reviewQueueCount > 0 && !isSuperAdmin && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[11px] font-semibold rounded-full animate-notification-glow">
                  {reviewQueueCount > 99 ? '99+' : reviewQueueCount}
                </span>
              )}
              {item.name === 'My Approval Queue' && approvalQueueCount > 0 && !isSuperAdmin && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[11px] font-semibold rounded-full animate-notification-glow">
                  {approvalQueueCount > 99 ? '99+' : approvalQueueCount}
                </span>
              )}
              {item.name === 'Quotations' && quotationAttentionCount > 0 && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[11px] font-semibold rounded-full animate-notification-glow">
                  {quotationAttentionCount > 99 ? '99+' : quotationAttentionCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Settings Section */}
        <div className="mt-8">
          <div className="px-4 py-2">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Settings
            </h3>
          </div>
          <nav className="space-y-1">
            {settingsNavigation.filter(item => canAccess(item.requiredPermission)).map((item) => (
              <NavLink
                key={item.name}
                to={item.path}
                className={({ isActive }) => getLinkClass(isActive)}
              >
                <span className="text-current">{item.icon}</span>
                {item.name}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Request License Button */}
      {/* <div className="p-6 border-t border-gray-200">
        <Link
          to="/license-request"
          className="block w-full py-3 px-4 bg-emerald-400 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-colors text-center"
        >
          Request a licence
        </Link>
      </div> */}
    </div>
  );
};

export default Sidebar;